/**
 * Fletcher's check on the ticket sheet (YAG-1077), beside Proctor's review:
 *
 *   Fletcher's check  [Found a problem]                         Open the case
 *   Tested 800d063 · the current head · 3 of 4 steps held
 *   [▶ Watch the video · 0:23]          (plays in place, at the sheet's width)
 *   ▢ STEP 1 · HELD   The page loads at its new path        Watch from 0:02
 *   ▢ STEP 4 · DID NOT HOLD   The flow diagram shows ...    Watch from 0:09
 *   [Fix these] [Recheck]
 *   Nothing goes to Wright until someone asks. ...
 *
 * and on the Receipt, the check's one line and its video
 * (`fletcherReceiptLine`, `FletcherReceiptVideo`).
 *
 * Fix these and Recheck are `POST /api/work-items/:id/qa/fix` and
 * `.../qa/recheck` (`itemActions/fletcherActs.ts`): 202 when taken; 409 with a
 * sentence when there is nothing to act on, 502 when GitHub cannot be read,
 * 403/404 like Request changes, each with its sentence, which the card says in
 * place.
 */
import { useState, type RefObject } from "react";
import { Play } from "lucide-react";
import {
  qaCheckOnCurrentHead,
  qaVideoTime,
  type QaEvidence,
  type TicketQaCheck,
} from "@yagni/shared";
import { Btn } from "../queue/primitives.js";
import { Chip } from "../ui/chip.js";
import { captureUiError } from "../sentry.js";
import { WorkerMark } from "../workers/WorkerMark.js";
import { QaSteps, qaCaptureExpired, useQaVideoSeek } from "./QaSteps.js";
import { useWorkerLookup } from "./useWorkerLookup.js";
import "./fletcherCheck.css";

/**
 * True with or without Fletcher's comment on the pull request (that comment is
 * off by default): `@yagni fix` / `@yagni recheck` work as any comment a person
 * with write access leaves there (`qa/fletcherCommand.ts`).
 */
export const FLETCHER_CHECK_HINT =
  "Nothing goes to Wright until someone asks. On the pull request, a comment of @yagni fix or @yagni recheck does the same.";

type Tone = "neutral" | "success" | "honesty";

function short(sha: string | null): string | null {
  return sha ? sha.slice(0, 7) : null;
}

function clock(iso: string | null): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  if (at.toDateString() === new Date().toDateString())
    return at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return at.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** How many steps held of those that checked something; null when none did. */
function stepTally(check: TicketQaCheck): { held: number; checked: number } | null {
  const checked = check.steps.filter(
    (step) => step.outcome === "passed" || step.outcome === "failed",
  );
  if (checked.length === 0) return null;
  return {
    held: checked.filter((step) => step.outcome === "passed").length,
    checked: checked.length,
  };
}

/** The problems a check found: its failed steps, else its written findings. */
function problemCount(check: TicketQaCheck): number {
  const failed = check.steps.filter((step) => step.outcome === "failed").length;
  return failed > 0 ? failed : check.findings.length;
}

export function fletcherBadge(check: TicketQaCheck): { text: string; tone: Tone } {
  switch (check.state) {
    case "testing":
      return { text: "Testing now", tone: "neutral" };
    case "passed":
      return { text: "Passed", tone: "success" };
    case "findings": {
      const count = problemCount(check);
      return {
        text: count > 1 ? `Found ${count} problems` : "Found a problem",
        tone: "honesty",
      };
    }
    case "blocked":
      return { text: "Blocked", tone: "honesty" };
    case "incomplete":
      return { text: "Incomplete", tone: "honesty" };
    default:
      return { text: "Could not finish", tone: "honesty" };
  }
}

/** Which head the check tested, against the pull request's head now; unknown says nothing. */
function headWord(check: TicketQaCheck): string | null {
  const current = qaCheckOnCurrentHead(check);
  if (current === null) return null;
  return current ? "the current head" : "an older head";
}

/** "Tested 800d063 · the current head · 3 of 4 steps held". */
export function fletcherMetaLine(check: TicketQaCheck): string {
  const sha = short(check.headSha);
  if (check.state === "testing") {
    const started = clock(check.startedAt);
    return [sha ? `Testing ${sha}` : "Testing the pull request", started && `started ${started}`]
      .filter(Boolean)
      .join(" · ");
  }
  const tally = stepTally(check);
  return [
    sha ? `Tested ${sha}` : "Tested the pull request",
    headWord(check),
    tally ? `${tally.held} of ${tally.checked} steps held` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** The Receipt's line: "Fletcher passed 4 of 4 steps at 800d063". Null while it tests. */
export function fletcherReceiptLine(check: TicketQaCheck | null): string | null {
  if (!check || check.state === "testing") return null;
  const at = short(check.headSha);
  const tally = stepTally(check);
  const where = at ? ` at ${at}` : "";
  switch (check.state) {
    case "passed":
      return tally
        ? `Fletcher passed ${tally.held} of ${tally.checked} steps${where}`
        : `Fletcher passed${where}`;
    case "findings": {
      const count = problemCount(check);
      const found = count > 1 ? `found ${count} problems` : "found a problem";
      return `Fletcher ${found}${where}${tally ? ` · ${tally.held} of ${tally.checked} steps held` : ""}`;
    }
    case "blocked":
      return `Fletcher was blocked${where}`;
    case "incomplete":
      return `Fletcher could not complete the check${where}`;
    default:
      return `Fletcher could not finish the check${where}`;
  }
}

function videoLength(video: QaEvidence): string | null {
  return typeof video.durationSeconds === "number" && video.durationSeconds > 0
    ? qaVideoTime(video.durationSeconds)
    : null;
}

/**
 * The check's recording, opened in place at the width of whatever holds it.
 * `seekTo` lets the steps open it at a step; `label` names the closed button.
 */
function InPlaceVideo({
  video,
  label,
  open,
  onOpen,
  onClose,
  player,
  onLoadedMetadata,
}: {
  video: QaEvidence;
  label: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  player: RefObject<HTMLVideoElement | null>;
  onLoadedMetadata: () => void;
}) {
  const [failed, setFailed] = useState(false);
  if (qaCaptureExpired(video)) {
    return <p className="fc-meta">The recording has expired. The case keeps the written report.</p>;
  }
  if (!open) {
    const length = videoLength(video);
    return (
      <Btn variant="secondary" size="sm" className="fc-watch" onClick={onOpen}>
        <Play aria-hidden="true" className="h-3 w-3" />
        {length ? `${label} · ${length}` : label}
      </Btn>
    );
  }
  return (
    <div className="qa-video">
      <video
        ref={player}
        controls
        preload="metadata"
        src={video.url}
        aria-label="Fletcher's recording"
        onError={() => setFailed(true)}
        onLoadedMetadata={onLoadedMetadata}
      />
      {failed && (
        <p role="status" className="fc-meta">
          The recording could not be loaded. Refresh to check current access.
        </p>
      )}
      <button type="button" className="fc-link" onClick={onClose}>
        Hide the video
      </button>
    </div>
  );
}

type Verb = "fix" | "recheck";

const VERB_LABEL: Record<Verb, string> = { fix: "Fix these", recheck: "Recheck" };
const VERB_DONE: Record<Verb, string> = {
  fix: "Sent to Wright. It fixes these on the pull request's head.",
  recheck: "Fletcher is testing the pull request's current head again.",
};

/** The sentence a refused act says in place: the server's own when it sent one. */
async function refusalOf(res: Response, verb: Verb): Promise<string> {
  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  if (typeof body?.error === "string" && body.error.trim()) return body.error.trim();
  return `${VERB_LABEL[verb]} did not go through. Try again.`;
}

function FletcherActs({
  workItemId,
  requestId,
  onActed,
}: {
  workItemId: string;
  requestId: string;
  onActed: () => void;
}) {
  const [busy, setBusy] = useState<Verb | null>(null);
  const [done, setDone] = useState<{ verb: Verb; requestId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const act = async (verb: Verb) => {
    if (busy) return;
    setBusy(verb);
    setError(null);
    try {
      const res = await fetch(`/api/work-items/${encodeURIComponent(workItemId)}/qa/${verb}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (!res.ok) {
        setError(await refusalOf(res, verb));
        return;
      }
      setDone({ verb, requestId });
      window.dispatchEvent(new Event("tickets.invalidate"));
      onActed();
    } catch (err) {
      captureUiError(err, { area: "Work", operation: `fletcher_check_${verb}`, workItemId });
      setError(`${VERB_LABEL[verb]} did not go through. Try again.`);
    } finally {
      setBusy(null);
    }
  };
  // Taken: said once, until the next check replaces this one.
  if (done && done.requestId === requestId) {
    return (
      <div className="fc-acts">
        <p className="fc-done" role="status">
          {VERB_DONE[done.verb]}
        </p>
      </div>
    );
  }
  return (
    <div className="fc-acts">
      <div className="fc-acts-verbs">
        <Btn variant="primary" size="sm" disabled={busy != null} onClick={() => void act("fix")}>
          {busy === "fix" ? "Fix these…" : "Fix these"}
        </Btn>
        <Btn
          variant="secondary"
          size="sm"
          disabled={busy != null}
          onClick={() => void act("recheck")}
        >
          {busy === "recheck" ? "Recheck…" : "Recheck"}
        </Btn>
      </div>
      {error && (
        <p className="fc-error" role="alert">
          {error}
        </p>
      )}
      <p className="fc-hint">{FLETCHER_CHECK_HINT}</p>
    </div>
  );
}

export function FletcherCheckCard({
  check,
  workItemId,
  onOpenCase,
  onActed,
}: {
  check: TicketQaCheck | null;
  workItemId: string;
  onOpenCase: (caseId: string) => void;
  /** Read the check again: Fix these or Recheck was taken. */
  onActed: () => void;
}) {
  const workerOf = useWorkerLookup();
  const fletcher = workerOf("fletcher");
  const [videoOpen, setVideoOpen] = useState(false);
  const { player, seek, onLoadedMetadata } = useQaVideoSeek();
  if (!check) return null;
  const badge = fletcherBadge(check);
  const video = check.video;
  const playable = video != null && !qaCaptureExpired(video);
  const canAct = check.state === "findings" && qaCheckOnCurrentHead(check) !== false;
  const showFindings = check.steps.length === 0 && check.findings.length > 0;
  return (
    <section className="fc-card" aria-label="Fletcher's check" data-testid="fletcher-check">
      <div className="fc-head">
        {fletcher && (
          <WorkerMark
            avatar={fletcher.avatar}
            name={fletcher.displayName}
            slug={fletcher.slug}
            size="sm"
          />
        )}
        <h3 className="fc-title">Fletcher's check</h3>
        <Chip tone={badge.tone} data-testid="fletcher-check-badge">
          {badge.text}
        </Chip>
        <button type="button" className="fc-open" onClick={() => onOpenCase(check.caseId)}>
          Open the case
        </button>
      </div>
      <p className="fc-meta" data-testid="fletcher-check-meta">
        {fletcherMetaLine(check)}
      </p>
      {video && (
        <InPlaceVideo
          key={video.id}
          video={video}
          label="Watch the video"
          open={videoOpen}
          onOpen={() => setVideoOpen(true)}
          onClose={() => setVideoOpen(false)}
          player={player}
          onLoadedMetadata={onLoadedMetadata}
        />
      )}
      <QaSteps
        steps={check.steps}
        {...(playable
          ? {
              onWatch: (seconds: number) => {
                setVideoOpen(true);
                seek(seconds);
              },
            }
          : {})}
      />
      {showFindings && (
        <ul className="fc-findings" aria-label="What Fletcher found">
          {check.findings.map((finding, at) => (
            <li key={at}>{finding.title || finding.detail}</li>
          ))}
        </ul>
      )}
      {canAct && (
        <FletcherActs
          key={check.requestId}
          workItemId={workItemId}
          requestId={check.requestId}
          onActed={onActed}
        />
      )}
    </section>
  );
}

/** The Receipt's video: Fletcher's last check, played in place. */
export function FletcherReceiptVideo({ check }: { check: TicketQaCheck | null }) {
  const [open, setOpen] = useState(false);
  const { player, onLoadedMetadata } = useQaVideoSeek();
  const video = check && check.state !== "testing" ? check.video : null;
  if (!video || qaCaptureExpired(video)) return null;
  return (
    <div className="fc-receipt-video span">
      <InPlaceVideo
        key={video.id}
        video={video}
        label="Watch Fletcher's video"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        player={player}
        onLoadedMetadata={onLoadedMetadata}
      />
    </div>
  );
}
